import React, { useContext } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik, Form, FormikProvider } from 'formik';
import { NumericFormat } from 'react-number-format';
import {
  Button,
  TextField,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// contexts
import { EventContext } from '../../../contexts/Event';
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
// services
import { createVoucher, updateVoucher } from '../../../services/Voucher';

VoucherFormDialog.propTypes = {
  openVoucherFormDialog: PropTypes.bool.isRequired,
  setOpenVoucherFormDialog: PropTypes.func.isRequired,
  setVouchers: PropTypes.func.isRequired,
  voucher: PropTypes.object
};

export default function VoucherFormDialog({
  openVoucherFormDialog,
  setOpenVoucherFormDialog,
  setVouchers,
  voucher
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);

  const voucherValues = {
    identifier: 'code',
    discount: voucher?.discount || '',
    code: voucher?.code || '',
    limit: voucher?.limit || ''
  };

  const VoucherSchema = Yup.object().shape({
    identifier: Yup.string(),
    discount: Yup.number().integer().positive().required('Discount is required'),
    limit: Yup.number().integer().positive().required('Limit is required'),
    code: Yup.string().required('Code is required')
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: voucherValues,
    validationSchema: VoucherSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const payload = {
          identifier: values.identifier,
          discount: values.discount,
          code: values.code,
          limit: values.limit,
          event: {
            _id: event._id,
            name: event.name,
            translation: {
              pt: {
                name: event.translation.pt.name
              }
            }
          }
        };

        if (voucher?._id) {
          await updateVoucher({ headers, payload, voucherId: voucher._id });

          setVouchers((vouchers) =>
            vouchers.map((currentVoucher) => {
              if (currentVoucher._id === voucher._id) {
                return {
                  ...voucher,
                  identifier: values.identifier,
                  discount: values.discount,
                  code: values.code,
                  limit: values.limit
                };
              }

              return currentVoucher;
            })
          );
        } else {
          const voucher = await createVoucher({ headers, payload });
          setVouchers((vouchers) => [...vouchers, voucher]);
        }

        handleClose();
        actions.resetForm();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenVoucherFormDialog(false);
  };

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Dialog open={openVoucherFormDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
            {voucher?.name ? 'Edit' : 'Create'} Voucher
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Stack spacing={2} pt={1}>
              <TextField
                fullWidth
                label="Code*"
                {...getFieldProps('code')}
                error={Boolean(touched.code && errors.code)}
                helperText={touched.code && errors.code}
                disabled={Boolean(voucher?.code)}
              />

              <NumericFormat
                fullWidth
                label="Discount (0 - 100%)*"
                {...getFieldProps(`discount`)}
                error={Boolean(touched.discount && errors.discount)}
                helperText={touched.discount && errors.discount}
                decimalScale={0}
                customInput={TextField}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />

              <NumericFormat
                fullWidth
                label="Limit*"
                {...getFieldProps('limit')}
                error={Boolean(touched.limit && errors.limit)}
                helperText={touched.limit && errors.limit}
                decimalScale={0}
                customInput={TextField}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
